import {
  ApplicantOfferStatus,
  ApplicationStatus,
  ApplyDocTypes,
  IApplyDashboardDocumentConfig,
  IApplyDashboardTabConfig,
  IApplyDashboardTabs,
  IApplyDashboardTabStatusMap,
} from 'types';
import { DefaultDashboardTabIds } from './index.js';

/**
 * These types are used exclusively in the student apply frontend for the offers dashboard, to configure
 * the tabs shown to the user, and to configure which statuses are included in each tab.
 *
 * These types are not to be used in the backend apply services.
 *
 * In future these types can be moved to the apply config as part of configurable dashboard tabs per university
 * / configurable apply stages per university.
 */

const standardTabs: IApplyDashboardTabs = Object.freeze({
  ['Pipeline']: [
    { label: 'Leads', id: DefaultDashboardTabIds.LEADS },
    { label: 'Applied', id: DefaultDashboardTabIds.APPLIED },
    { label: 'Conditional', id: DefaultDashboardTabIds.CONDITIONAL },
    { label: 'Unconditional', id: DefaultDashboardTabIds.UNCONDITIONAL },
  ],
  ['Outcome']: [
    { label: 'Completed', id: DefaultDashboardTabIds.COMPLETED },
    { label: 'Closed', id: DefaultDashboardTabIds.CLOSED },
  ],
});

const agentTabs: IApplyDashboardTabs = Object.freeze({
  ['Pipeline']: [
    { label: 'Leads', id: DefaultDashboardTabIds.LEADS },
    { label: 'Applied', id: DefaultDashboardTabIds.APPLIED },
    { label: 'Conditional', id: DefaultDashboardTabIds.CONDITIONAL },
    { label: 'Unconditional', id: DefaultDashboardTabIds.UNCONDITIONAL },
  ],
  ['Outcome']: [
    { label: 'Completed', id: DefaultDashboardTabIds.COMPLETED },
    { label: 'Closed', id: DefaultDashboardTabIds.CLOSED },
  ],
});

/**
 * Maps tab IDs to document types for the purpose of logging stage changes more clearly in the audit log.
 * This could be hard coded into the helper method, I've decided to keep it here for clarity and future
 * integration into the apply config alongside the other dashboard tab configurations.
 */
export const TabIdDocumentMap: IApplyDashboardDocumentConfig = Object.freeze({
  [DefaultDashboardTabIds.LEADS]: [ApplyDocTypes.ApplicationSummary],
  [DefaultDashboardTabIds.APPLIED]: [ApplyDocTypes.OfferSummary],
  [DefaultDashboardTabIds.CONDITIONAL]: [ApplyDocTypes.OfferSummary],
  [DefaultDashboardTabIds.UNCONDITIONAL]: [ApplyDocTypes.OfferSummary],
  [DefaultDashboardTabIds.COMPLETED]: [ApplyDocTypes.OfferSummary],
  [DefaultDashboardTabIds.CLOSED]: [ApplyDocTypes.OfferSummary, ApplyDocTypes.ApplicationSummary],
});

/**
 * Maps tab IDs to application/offer statuses for counting totals and setting QSPs that query the backend for the dashboard
 * rows.
 */
export const TabIdStageMap: IApplyDashboardTabStatusMap = Object.freeze({
  [DefaultDashboardTabIds.LEADS]: {
    applicationStatuses: [ApplicationStatus.InProgress, ApplicationStatus.Completed],
    offerStatuses: [],
  },
  [DefaultDashboardTabIds.APPLIED]: {
    offerStatuses: [ApplicantOfferStatus.Applied],
    applicationStatuses: [],
  },
  [DefaultDashboardTabIds.CONDITIONAL]: { offerStatuses: [ApplicantOfferStatus.Conditional], applicationStatuses: [] },
  [DefaultDashboardTabIds.UNCONDITIONAL]: {
    offerStatuses: [ApplicantOfferStatus.Unconditional],
    applicationStatuses: [],
  },
  [DefaultDashboardTabIds.COMPLETED]: { offerStatuses: [ApplicantOfferStatus.Completed], applicationStatuses: [] },
  [DefaultDashboardTabIds.CLOSED]: {
    offerStatuses: [ApplicantOfferStatus.Rejected, ApplicantOfferStatus.Withdrawn],
    applicationStatuses: [ApplicationStatus.Rejected, ApplicationStatus.Withdrawn],
  },
});

/**
 * Maps tab IDs to application/offer statuses for counting totals and setting QSPs that query the backend for the dashboard
 * rows for Agent/AgencyAdmin/Partner roles.
 */
export const TabIdStageMapForAgent: IApplyDashboardTabStatusMap = Object.freeze({
  [DefaultDashboardTabIds.LEADS]: {
    applicationStatuses: [ApplicationStatus.InProgress, ApplicationStatus.Completed],
    offerStatuses: [],
  },
  [DefaultDashboardTabIds.APPLIED]: {
    offerStatuses: [ApplicantOfferStatus.Applied],
    applicationStatuses: [],
  },
  [DefaultDashboardTabIds.CONDITIONAL]: { offerStatuses: [ApplicantOfferStatus.Conditional], applicationStatuses: [] },
  [DefaultDashboardTabIds.UNCONDITIONAL]: {
    offerStatuses: [ApplicantOfferStatus.Unconditional],
    applicationStatuses: [],
  },
  [DefaultDashboardTabIds.COMPLETED]: { offerStatuses: [ApplicantOfferStatus.Completed], applicationStatuses: [] },
  [DefaultDashboardTabIds.CLOSED]: {
    offerStatuses: [ApplicantOfferStatus.Rejected, ApplicantOfferStatus.Withdrawn],
    applicationStatuses: [ApplicationStatus.Rejected, ApplicationStatus.Withdrawn],
  },
});

// Hard coded dashboard tabs configuration per user role.
// in future these are to be configured per university in the ApplyUniConfig.
export const dashboardTabs: IApplyDashboardTabConfig = Object.freeze({
  '[UniAdmin]': { tabs: standardTabs, map: TabIdStageMap, documentMap: TabIdDocumentMap },
  '[Uni]': { tabs: standardTabs, map: TabIdStageMap, documentMap: TabIdDocumentMap },
  '[UniLite]': { tabs: standardTabs, map: TabIdStageMap, documentMap: TabIdDocumentMap },
  '[UniSecure]': { tabs: standardTabs, map: TabIdStageMap, documentMap: TabIdDocumentMap },
  '[Admin]': { tabs: standardTabs, map: TabIdStageMap, documentMap: TabIdDocumentMap },
  '[Agent]': { tabs: agentTabs, map: TabIdStageMapForAgent, documentMap: TabIdDocumentMap },
  '[AgencyAdmin]': { tabs: agentTabs, map: TabIdStageMapForAgent, documentMap: TabIdDocumentMap },
  '[Partner]': { tabs: agentTabs, map: TabIdStageMapForAgent, documentMap: TabIdDocumentMap },
});
